import React, { Fragment, useState } from 'react'
import './FileDisplayView.scss'
import EmptyFolder from '../EmptyFolder'
import { Spinner } from '../../../../../components'
import VersionHistoryModal from '../VersionHistoryModal'
import ManagePermissionsModal from '../ManagePermissionsModal'
import AuditModal from '../AuditModal/AuditModal'
import FileGrid from './components/FileGrid'
import FileList from './components/FileList'

const FileDisplayView = ({
  isFolder,
  orgUserList,
  user,
  searchType,
  defaultQuery = {},
  data = [],
  selectedItems = [],
  onSelectionChange,
  onAction,
  onItemClick,
  loading = true,
  viewType = 'grid',
  setOnItemClicked,
  refreshData,
  aiProcessingItems = new Set(),
  isDownloadCenter
}) => {
  const [versionModalVisible, setVersionModalVisible] = useState(false)
  const [versionFile, setVersionFile] = useState(null)
  const [hoveredItemId, setHoveredItemId] = useState(null)
  const [
    managePermissionsModalVisible,
    setManagePermissionsModalVisible
  ] = useState({ visible: false, id: null })
  const [auditModal, setAuitModal] = useState({ visible: false, id: null })
  const activeOrganizationId =
    user && user.info && user.info.organization && user.info.organization.id
  const userId = user && user.info && user.info.id
  const isSuperAdmin = user && user.info && user.info.is_super_admin
  const isAdmin =
    user && user.info && user.info.role && user.info.role.name === 'Admin'

  // Helper functions for selection
  const shouldShowCheckbox = itemId => {
    const hasSelection = selectedItems.length > 0
    const isHovered = hoveredItemId === itemId
    return hasSelection || isHovered
  }
  const folders = (isFolder && data) || []
  const files = (!isFolder && data) || []
  const folderIds = folders.map(folder => folder.id)
  const fileIds = files.map(file => file.id)
  const selectedFolders = selectedItems.filter(id => folderIds.includes(id))
  const selectedFiles = selectedItems.filter(id => fileIds.includes(id))
  const isAllFoldersSelected =
    folderIds.length > 0 && folderIds.every(id => selectedItems.includes(id))
  const isAllFilesSelected =
    fileIds.length > 0 && fileIds.every(id => selectedItems.includes(id))
  const isIndeterminateFolders =
    selectedFolders.length > 0 && selectedFolders.length < folderIds.length
  const isIndeterminateFiles =
    selectedFiles.length > 0 && selectedFiles.length < fileIds.length
  const handleSelectAllFolders = () => {
    if (!onSelectionChange) return
    const newSelection = isAllFoldersSelected
      ? selectedItems.filter(id => !folderIds.includes(id))
      : [...selectedItems.filter(id => !folderIds.includes(id)), ...folderIds]
    onSelectionChange(newSelection)
  }
  const handleSelectAllFiles = () => {
    if (!onSelectionChange) return
    const newSelection = isAllFilesSelected
      ? selectedItems.filter(id => !fileIds.includes(id))
      : [...selectedItems.filter(id => !fileIds.includes(id)), ...fileIds]
    onSelectionChange(newSelection)
  }
  const handleItemClick = (item, event) => {
    setOnItemClicked(item.id)
    if (
      event &&
      event.target.closest &&
      (event.target.closest('.item-actions') ||
        event.target.closest('.file-card-actions'))
    ) {
      return
    }
    if (selectedItems.length === 0) {
      if (item.media_format === 'FOLDER') {
        onItemClick && onItemClick(item)
      } else {
        onAction && onAction('view', item)
      }
    } else {
      if (selectedItems.includes(item.id)) {
        onSelectionChange &&
          onSelectionChange(selectedItems.filter(id => id !== item.id))
      } else {
        onSelectionChange && onSelectionChange([...selectedItems, item.id])
      }
    }
  }

  if (loading) return <Spinner size="small" height="200px" />

  return (
    <div className="file-display-view">
      <div
        className={viewType === 'grid' ? 'grid-view-container' : 'list-view'}
      >
        {loading ? (
          <Spinner size="small" height="200px" />
        ) : (
          <Fragment>
            {viewType === 'grid' ? (
              <FileGrid
                isFolder={isFolder}
                folders={folders}
                files={files}
                selectedItems={selectedItems}
                onSelectionChange={onSelectionChange}
                onAction={onAction}
                user={user}
                orgUserList={orgUserList}
                defaultQuery={defaultQuery}
                userId={userId}
                isSuperAdmin={isSuperAdmin}
                isAdmin={isAdmin}
                aiProcessingItems={aiProcessingItems}
                setVersionFile={setVersionFile}
                setVersionModalVisible={setVersionModalVisible}
                setManagePermissionsModalVisible={
                  setManagePermissionsModalVisible
                }
                setAuitModal={setAuitModal}
                hoveredItemId={hoveredItemId}
                setHoveredItemId={setHoveredItemId}
                shouldShowCheckbox={shouldShowCheckbox}
                isAllFoldersSelected={isAllFoldersSelected}
                isIndeterminateFolders={isIndeterminateFolders}
                handleSelectAllFolders={handleSelectAllFolders}
                isAllFilesSelected={isAllFilesSelected}
                isIndeterminateFiles={isIndeterminateFiles}
                handleSelectAllFiles={handleSelectAllFiles}
                searchType={searchType}
                isDownloadCenter={isDownloadCenter}
              />
            ) : (
              <FileList
                isFolder={isFolder}
                folders={folders}
                files={files}
                selectedItems={selectedItems}
                onSelectionChange={onSelectionChange}
                onAction={onAction}
                user={user}
                orgUserList={orgUserList}
                defaultQuery={defaultQuery}
                userId={userId}
                isSuperAdmin={isSuperAdmin}
                isAdmin={isAdmin}
                aiProcessingItems={aiProcessingItems}
                setVersionFile={setVersionFile}
                setVersionModalVisible={setVersionModalVisible}
                setManagePermissionsModalVisible={
                  setManagePermissionsModalVisible
                }
                setAuitModal={setAuitModal}
                hoveredItemId={hoveredItemId}
                setHoveredItemId={setHoveredItemId}
                shouldShowCheckbox={shouldShowCheckbox}
                isAllFoldersSelected={isAllFoldersSelected}
                isIndeterminateFolders={isIndeterminateFolders}
                handleSelectAllFolders={handleSelectAllFolders}
                isAllFilesSelected={isAllFilesSelected}
                isIndeterminateFiles={isIndeterminateFiles}
                handleSelectAllFiles={handleSelectAllFiles}
                searchType={searchType}
                handleItemClick={handleItemClick}
                isDownloadCenter={isDownloadCenter}
              />
            )}
          </Fragment>
        )}
      </div>
      <VersionHistoryModal
        user={user}
        orgUserList={orgUserList}
        visible={versionModalVisible}
        onClose={() => setVersionModalVisible(false)}
        file={versionFile}
      />
      <AuditModal
        visible={auditModal.visible}
        onClose={() => setAuitModal({ visible: false, id: null })}
        id={auditModal.id}
      />
      <ManagePermissionsModal
        refreshData={refreshData}
        visible={managePermissionsModalVisible.visible}
        onClose={() =>
          setManagePermissionsModalVisible({ visible: false, id: null })
        }
        id={managePermissionsModalVisible.id}
        orgUserList={orgUserList}
        organizationId={activeOrganizationId}
      />
    </div>
  )
}

export default FileDisplayView
